import React, { useState } from 'react';
import axios from 'axios';
import { Row, Col, Form, Button, Table, Badge } from 'react-bootstrap';
import dayjs from '../../../lib/day';
import PrivateLayout from '../../layouts/PrivateLayout';
import Panel from '../../common/Panel';
import Pagination from '../../common/Pagination';
import { useFetchVaAttemptGroup, useFetchVaUsers, useFetchVaUser, useFetchVaBlocklist } from '../../../services/vaCallAttempts';
import { COLUMNS } from '../../../components/dashboard/admin/SalesDashboard';

const statusToVariant = (s) => {
  switch (s) {
    case 'ok':
      return 'success';
    case 'no_info':
      return 'secondary';
    case 'sys_unavail':
      return 'warning';
    case 'error':
    default:
      return 'danger';
  }
};

const GroupAttemptsRow = ({ groupId }) => {
  const { data, isLoading } = useFetchVaAttemptGroup(groupId);
  const attempts = data && data.attempts ? data.attempts : [];
  if (isLoading) return <div className="p-2 text-muted">Loading attempts…</div>;
  if (!attempts.length) return <div className="p-2 text-muted">No attempts</div>;
  return (
    <div className="p-2">
      <Table size="sm" striped={true} bordered={true} className="mb-0">
        <thead>
          <tr>
            <th>Ordinal</th>
            <th>Result</th>
            <th>Retell</th>
            <th>When</th>
          </tr>
        </thead>
        <tbody>
          {attempts.map((a) => (
            <tr key={a.vaCallAttemptId}>
              <td>{a.vaPaymentAttemptOrdinal}</td>
              <td>
                <Badge bg={statusToVariant(a.vaPaymentResult)}>{a.vaPaymentResult}</Badge>
                {a.vaPaymentResult === 'ok' && a.noExactMatch && (
                  <Badge bg="warning" text="dark" className="ms-1" title="Amount not in VA payment matrix - payment not recorded">
                    No Match
                  </Badge>
                )}
              </td>
              <td>
                {a.vaPaymentRetellCallId ? (
                  <a
                    href={`https://dashboard.retellai.com/call-history?history=${encodeURIComponent(
                      a.vaPaymentRetellCallId,
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View
                  </a>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td>{a.createdAt ? dayjs.utc(a.createdAt).local().format('MM/DD/YYYY h:mm A') : ''}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const UserGroupsRow = ({ ssn }) => {
  const { data, isLoading } = useFetchVaUser(ssn);
  const groups = data && data.groups ? data.groups : [];
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [reason, setReason] = useState('');
  const onBlock = async () => {
    try {
      await axios.post('/retell-admin/va-users/block', { ssn, reason: reason || undefined });
      window.dispatchEvent(new Event('focus'));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };
  const toggleGroup = (gid) => {
    const next = new Set(expandedGroups);
    if (next.has(gid)) next.delete(gid);
    else next.add(gid);
    setExpandedGroups(next);
  };
  if (isLoading) return <div className="p-2 text-muted">Loading user groups…</div>;
  if (!groups.length) return <div className="p-2 text-muted">No groups</div>;
  return (
    <div className="p-2">
      <div className="mb-2 d-flex gap-2 align-items-center flex-wrap">
        <Form.Control
          size="sm"
          style={{ maxWidth: 260 }}
          placeholder="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <Button size="sm" variant="outline-danger" onClick={onBlock}>
          Block User
        </Button>
      </div>
      <Table size="sm" striped={true} bordered={true} className="mb-0">
        <thead>
          <tr>
            <th></th>
            <th>Last Result</th>
            <th>Attempts</th>
            <th>Last Attempt</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const isOpen = expandedGroups.has(g.groupId);
            return (
              <React.Fragment key={g.groupId}>
                <tr>
                  <td width="30">
                    <Button size="sm" variant="light" onClick={() => toggleGroup(g.groupId)}>
                      {isOpen ? '−' : '+'}
                    </Button>
                  </td>
                  <td>
                    <Badge bg={statusToVariant(g.lastResult)}>{g.lastResult}</Badge>
                    {g.lastResult === 'ok' && g.lastNoExactMatch && (
                      <Badge bg="warning" text="dark" className="ms-1" title="Amount not in VA payment matrix - no payment recorded">
                        No Match
                      </Badge>
                    )}
                  </td>
                  <td>{g.attemptCount}</td>
                  <td>{g.lastCreatedAt ? dayjs.utc(g.lastCreatedAt).local().format('MM/DD/YYYY h:mm A') : ''}</td>
                </tr>
                {isOpen ? (
                  <tr>
                    <td colSpan={4}>
                      <GroupAttemptsRow groupId={g.groupId} />
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

const BlocklistTable = () => {
  const { data, isLoading } = useFetchVaBlocklist();
  const entries = (data && data.entries) || [];
  if (isLoading) return <div className="p-2 text-muted">Loading blocklist…</div>;
  if (!entries.length) return <div className="p-2 text-muted">No blocked users</div>;
  return (
    <div className="p-2">
      <Table size="sm" striped={true} bordered={true} className="mb-0">
        <thead>
          <tr>
            <th>SSN</th>
            <th>Reason</th>
            <th>Users</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.ssn || 'blank-ssn'}>
              <td>{e.ssn || <span className="text-muted">(blank)</span>}</td>
              <td>{e.reason || <span className="text-muted">—</span>}</td>
              <td>
                {Array.isArray(e.users) && e.users.length > 0 ? (
                  e.users.map((u, idx) => {
                    const name = `${u.userFirstName || ''} ${u.userLastName || ''}`.trim() || 'User';
                    const content = u.claimId ? (
                      <a key={String(u.userId || idx)} href={`/admin/profile/${u.claimId}`}>{name}</a>
                    ) : (
                      <span key={String(u.userId || idx)}>{name}</span>
                    );
                    return (
                      <React.Fragment key={`bluser-${e.ssn}-${u.userId || idx}`}>
                        {idx > 0 ? ', ' : null}
                        {content}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const VaCalls = () => {
  const [filters, setFilters] = useState({
    page: 1,
    pageSize: 25,
    startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    claimStatus: '',
    result: '',
    name: '',
    notInitialized: false,
  });
  const [expanded, setExpanded] = useState(new Set());
  const [showBlocklist, setShowBlocklist] = useState(false);

  const { isLoading, data } = useFetchVaUsers(
    filters.page,
    filters.pageSize,
    filters.startDate,
    filters.endDate,
    filters.claimStatus || null,
    filters.result || null,
    null,
    null,
    filters.notInitialized,
    filters.name || null,
  );

  const totals = data && data.totals ? data.totals : {};
  const users = data && data.users ? data.users : [];
  const pagination = data && data.pagination ? data.pagination : { total: 0 };

  const refreshNumbers = async () => {
    try {
      await axios.post('/retell-admin/numbers/refresh');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const toggle = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  return (
    <PrivateLayout>
      <Row>
        <Col>
          <Panel>
            <Row>
              <Col>
                <h2>VA Call Attempts</h2>
              </Col>
              <Col xs={12} md={8} className="text-end">
                <div className="d-flex gap-2 justify-content-end flex-wrap">
                  <Form.Control
                    type="search"
                    placeholder="Search by name or email"
                    value={filters.name}
                    onChange={(e) => setFilters({ ...filters, page: 1, name: e.target.value })}
                    style={{ maxWidth: 260 }}
                  />
                  <Form.Control
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({ ...filters, page: 1, startDate: e.target.value })}
                    style={{ maxWidth: 160 }}
                  />
                  <Form.Control
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({ ...filters, page: 1, endDate: e.target.value })}
                    style={{ maxWidth: 160 }}
                  />
                  <Form.Select
                    value={filters.claimStatus}
                    onChange={(e) => setFilters({ ...filters, page: 1, claimStatus: e.target.value })}
                    style={{ maxWidth: 180 }}
                  >
                    <option value="">All Statuses</option>
                    {COLUMNS.filter((c) => c.id !== 'leads').map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Select
                    value={filters.result}
                    onChange={(e) => setFilters({ ...filters, page: 1, result: e.target.value })}
                    style={{ maxWidth: 180 }}
                  >
                    <option value="">All Results</option>
                    <option value="ok">OK</option>
                    <option value="no_info">No Info</option>
                    <option value="sys_unavail">System Unavailable</option>
                    <option value="error">Error</option>
                  </Form.Select>
                  <Form.Check
                    type="switch"
                    id="notInitialized"
                    label="Not Initialized"
                    checked={!!filters.notInitialized}
                    onChange={(e) => setFilters({ ...filters, page: 1, notInitialized: e.target.checked })}
                  />
                  <Button variant="outline-primary" onClick={refreshNumbers}>
                    Refresh Retell Numbers
                  </Button>
                </div>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <div className="d-flex gap-4 flex-wrap">
                  <div>
                    <div className="text-muted">Users</div>
                    <div className="fs-5 fw-bold">{Number(totals.totalUsers || 0)}</div>
                  </div>
                  <div>
                    <div className="text-muted">Attempts</div>
                    <div className="fs-5 fw-bold">{Number(totals.totalAttempts || 0)}</div>
                  </div>
                  <div>
                    <div className="text-muted">OK</div>
                    <div className="fs-5 fw-bold">{Number(totals.okCount || 0)}</div>
                  </div>
                  <div>
                    <div className="text-muted">No Info</div>
                    <div className="fs-5 fw-bold">{Number(totals.noInfoCount || 0)}</div>
                  </div>
                  <div>
                    <div className="text-muted">System Unavailable</div>
                    <div className="fs-5 fw-bold">{Number(totals.sysUnavailCount || 0)}</div>
                  </div>
                  <div>
                    <div className="text-muted">Errors</div>
                    <div className="fs-5 fw-bold">{Number(totals.errorCount || 0)}</div>
                  </div>
                </div>
              </Col>
            </Row>

            <br />

            <Table hover={true}>
              <thead>
                <tr>
                  <th></th>
                  <th>User</th>
                  <th>Claim Status</th>
                  <th>Last Result</th>
                  <th>Attempts</th>
                  <th>Last Attempt</th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && users.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <p className="lead text-center">No Results Found</p>
                    </td>
                  </tr>
                ) : null}
                {users.map((u) => {
                  const label = COLUMNS.find((c) => c.id === u.claimStatus)?.label;
                  const isOpen = expanded.has(u.ssn);
                  return (
                    <React.Fragment key={u.ssn}>
                      <tr>
                        <td width="30">
                          <Button size="sm" variant="light" onClick={() => toggle(u.ssn)}>
                            {isOpen ? '−' : '+'}
                          </Button>
                        </td>
                        <td>
                          {u.claimId ? (
                            <a href={`/admin/profile/${u.claimId}`}>
                              {(u.userFirstName || '') + ' ' + (u.userLastName || '')}
                            </a>
                          ) : (
                            (u.userFirstName || '') + ' ' + (u.userLastName || '')
                          )}
                        </td>
                        <td>{label || u.claimStatus || ''}</td>
                        <td>
                          <Badge bg={statusToVariant(u.lastResult)}>{u.lastResult}</Badge>
                          {u.lastResult === 'ok' && u.lastNoExactMatch && (
                            <Badge bg="warning" text="dark" className="ms-1" title="Amount not in VA payment matrix - no payment recorded">
                              No Match
                            </Badge>
                          )}
                        </td>
                        <td>{u.attemptCount}</td>
                        <td>{u.lastCreatedAt ? dayjs.utc(u.lastCreatedAt).local().format('MM/DD/YYYY h:mm A') : ''}</td>
                      </tr>
                      {isOpen ? (
                        <tr>
                          <td colSpan={6}>
                            <UserGroupsRow ssn={u.ssn} />
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>

            {pagination.total > 0 ? (
              <Pagination
                page={filters.page}
                totalRows={pagination.total}
                rowsPerPage={filters.pageSize}
                onBack={() => setFilters({ ...filters, page: filters.page - 1 })}
                onForward={() => setFilters({ ...filters, page: filters.page + 1 })}
              />
            ) : null}

            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h4 className="mb-0">Blocklist</h4>
                <Button size="sm" variant="light" onClick={() => setShowBlocklist(!showBlocklist)}>
                  {showBlocklist ? '−' : '+'}
                </Button>
              </div>
              {showBlocklist ? <BlocklistTable /> : null}
            </div>
          </Panel>
        </Col>
      </Row>
    </PrivateLayout>
  );
};

export default VaCalls;
